import React, { useState, useEffect, useRef } from 'react';
import { Sun, Moon, MapPin, Clock, ToggleLeft, ToggleRight } from 'lucide-react';
import '../styles/animations.css';

import Starfield from './Starfield';
import SunProgressBar from './SunProgressBar/SunProgressBar';
import LocationModal, { presetLocations } from './LocationModal';
import CelebrationEffect from './CelebrationEffect';
import AdditionalInfoContainer from './AdditionalInfo/AdditionalInfoContainer';

import useSunTimes from '../hooks/useSunTimes';
import usePrayerTimes from '../hooks/usePrayerTimes';
import useWeather from '../hooks/useWeather';

import { getLocationTimeFromTimezone, getEstimatedLocationTime } from '../utils/timezoneHelper';

const SunriseSunsetApp = () => {
  const [coordinates, setCoordinates] = useState(() => {
    const saved = localStorage.getItem('sunriseApp.coordinates');
    return saved
      ? JSON.parse(saved)
      : { lat: -33.8688, lng: 151.2093, name: 'Sydney' };
  });

  const [locationTime, setLocationTime] = useState(new Date());
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationInput, setLocationInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const {
    sunrise, sunset, nextEvent, timeUntil, isDay,
    progress, specialCase, additionalInfo,
    isCelebrationMoment
  } = useSunTimes(coordinates);

  const {
    prayerTimes,
    tomorrowPrayerTimes,
    showPrayerMarkers,
    setShowPrayerMarkers
  } = usePrayerTimes(coordinates);

  const { weatherData, weatherLoading } = useWeather(coordinates);

  const [showCelebration, setShowCelebration] = useState(false);
  const celebrationTimerRef = useRef(null);

  // Countdown mode: 'prayer' (Fajr/Maghrib) or 'sun' (Sunrise/Sunset)
  const [countdownMode, setCountdownMode] = useState(() => {
    return localStorage.getItem('sunriseApp.countdownMode') || 'prayer';
  });

  useEffect(() => {
    localStorage.setItem('sunriseApp.countdownMode', countdownMode);
  }, [countdownMode]);

  // Update location time every second - prefer IANA timezone from prayer API
  useEffect(() => {
    const updateLocationTime = () => {
      const tz = prayerTimes?.timezone;
      const time = getLocationTimeFromTimezone(tz) || getEstimatedLocationTime(coordinates);
      setLocationTime(time);
    };
    updateLocationTime();
    const intervalId = setInterval(updateLocationTime, 1000);
    return () => clearInterval(intervalId);
  }, [coordinates, prayerTimes?.timezone]);

  // Show celebration for 5 seconds when a sunrise/sunset moment occurs.
  // Uses a ref for the timer so React effect cleanup doesn't cancel it
  // prematurely when isCelebrationMoment flips back to false on the next render.
  useEffect(() => {
    if (isCelebrationMoment) {
      setShowCelebration(true);
      if (celebrationTimerRef.current) clearTimeout(celebrationTimerRef.current);
      celebrationTimerRef.current = setTimeout(() => {
        setShowCelebration(false);
        celebrationTimerRef.current = null;
      }, 5000);
    }
  }, [isCelebrationMoment]);

  // Clean up celebration timer on unmount
  useEffect(() => {
    return () => {
      if (celebrationTimerRef.current) clearTimeout(celebrationTimerRef.current);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('sunriseApp.coordinates', JSON.stringify(coordinates));
  }, [coordinates]);

  const searchLocation = (query) => {
    setIsLoading(true);
    setTimeout(() => {
      const results = presetLocations.filter(loc =>
        loc.name.toLowerCase().includes(query.toLowerCase())
      );
      setSearchResults(results);
      setIsLoading(false);
    }, 500);
  };

  const handleLocationChange = (e) => {
    const query = e.target.value;
    setLocationInput(query);
    if (query.length > 2) {
      searchLocation(query);
    } else {
      setSearchResults([]);
    }
  };

  const selectLocation = (location) => {
    setCoordinates(location);
    setShowLocationModal(false);
    setLocationInput('');
    setSearchResults([]);
  };

  const showError = (msg) => {
    setErrorMessage(msg);
    setTimeout(() => setErrorMessage(null), 5000);
  };

  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      setIsLoading(true);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCoordinates({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            name: 'Current Location'
          });
          setIsLoading(false);
          setShowLocationModal(false);
        },
        (error) => {
          showError('Unable to retrieve your location: ' + error.message);
          setIsLoading(false);
        }
      );
    } else {
      showError('Geolocation is not supported by your browser');
    }
  };

  // Richer sky gradients based on progress
  const getSkyGradient = () => {
    if (specialCase === 'polarDay') return 'bg-gradient-to-b from-blue-300 to-cyan-200';
    if (specialCase === 'polarNight') return 'bg-gradient-to-b from-gray-900 to-indigo-950';

    if (!isDay) {
      if (progress > 0.85) return 'bg-gradient-to-b from-indigo-900 to-purple-900';
      return 'bg-gradient-to-b from-blue-950 to-indigo-950';
    } else {
      if (progress < 0.15) return 'bg-gradient-to-b from-orange-300 via-rose-300 to-blue-400';
      if (progress > 0.85) return 'bg-gradient-to-b from-blue-400 via-orange-300 to-rose-400';
      return 'bg-gradient-to-b from-blue-400 to-cyan-300';
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString([], {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const times = {
    sunrise, sunset, nextEvent, timeUntil,
    isDay, progress, specialCase, additionalInfo
  };

  // Compute countdown label + value based on mode (prayer or sun)
  // Helper: format a seconds value as "Xh Ym Zs"
  const fmtSec = (s) => {
    const hours = Math.floor(s / 3600);
    const mins = Math.floor((s % 3600) / 60);
    const secs = s % 60;
    return `${hours}h ${mins}m ${secs}s`;
  };

  // Helper: seconds from locationTime to a HH:MM time string, wrapping past midnight
  const secsToTimeStr = (timeStr) => {
    if (!timeStr || !timeStr.includes(':')) return null;
    const [h, m] = timeStr.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return null;
    const nowSec = locationTime.getHours() * 3600 + locationTime.getMinutes() * 60 + locationTime.getSeconds();
    let diff = h * 3600 + m * 60 - nowSec;
    if (diff <= 0) diff += 24 * 3600;
    return diff;
  };

  // Helper: true if a HH:MM time has already passed today in location time
  const hasPassed = (timeStr) => {
    if (!timeStr || !timeStr.includes(':')) return false;
    const [h, m] = timeStr.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return false;
    const nowSec = locationTime.getHours() * 3600 + locationTime.getMinutes() * 60 + locationTime.getSeconds();
    return nowSec >= h * 3600 + m * 60;
  };

  const getCountdown = () => {
    // ── Sun mode: pick whichever of sunrise/sunset is closer ──
    if (countdownMode === 'sun') {
      if (sunrise && sunset) {
        // Use the sunrise/sunset times formatted as HH:MM in location timezone
        const sunriseStr = sunrise.toLocaleTimeString('en-GB', {
          hour: '2-digit', minute: '2-digit', hour12: false,
          ...(prayerTimes?.timezone ? { timeZone: prayerTimes.timezone } : {})
        });
        const sunsetStr = sunset.toLocaleTimeString('en-GB', {
          hour: '2-digit', minute: '2-digit', hour12: false,
          ...(prayerTimes?.timezone ? { timeZone: prayerTimes.timezone } : {})
        });
        const secToRise = secsToTimeStr(sunriseStr);
        const secToSet = secsToTimeStr(sunsetStr);

        if (secToRise != null && secToSet != null) {
          const isSunsetNext = secToSet <= secToRise;
          return {
            label: isSunsetNext ? 'Time until Sunset' : 'Time until Sunrise',
            countdown: fmtSec(isSunsetNext ? secToSet : secToRise)
          };
        }
      }
      // Fallback to useSunTimes values
      return {
        label: nextEvent === 'sunrise' ? 'Time until Sunrise' : 'Time until Sunset',
        countdown: timeUntil
      };
    }

    // ── Prayer mode: pick whichever of Fajr/Maghrib is closer ──
    // Use the correct day's time: if a prayer has passed today, use tomorrow's time
    if (!prayerTimes) {
      return {
        label: nextEvent === 'sunrise' ? 'Time until Fajr' : 'Time until Iftar',
        countdown: timeUntil
      };
    }

    // Pick the right Fajr: if today's Fajr has passed, use tomorrow's
    const fajrTime = hasPassed(prayerTimes.fajr) && tomorrowPrayerTimes?.fajr
      ? tomorrowPrayerTimes.fajr : prayerTimes.fajr;
    // Pick the right Maghrib: if today's Maghrib has passed, use tomorrow's
    const maghribTime = hasPassed(prayerTimes.maghrib) && tomorrowPrayerTimes?.maghrib
      ? tomorrowPrayerTimes.maghrib : prayerTimes.maghrib;

    const secToFajr = secsToTimeStr(fajrTime);
    const secToMaghrib = secsToTimeStr(maghribTime);

    if (secToFajr == null || secToMaghrib == null) {
      return { label: isDay ? 'Time until Iftar' : 'Time until Fajr', countdown: timeUntil };
    }

    const isMaghribNext = secToMaghrib <= secToFajr;
    return {
      label: isMaghribNext ? 'Time until Iftar' : 'Time until Fajr',
      countdown: fmtSec(isMaghribNext ? secToMaghrib : secToFajr)
    };
  };

  const currentCountdown = getCountdown();

  return (
    <div className={`w-full h-screen overflow-hidden ${getSkyGradient()} transition-colors duration-1000`}>
      <Starfield isDay={isDay} />
      <CelebrationEffect show={showCelebration} type={nextEvent} />

      {/* Two-panel kiosk layout (>=1024px) / stacked mobile (<1024px) */}
      <div className="relative z-10 flex flex-col lg:flex-row h-full p-4 lg:p-6 gap-4 lg:gap-6">

        {/* LEFT PANEL: Main countdown */}
        <div className="flex-1 flex flex-col items-center justify-center min-w-0">
          {/* Sun/Moon icon */}
          <div className="mb-4">
            {isDay ? (
              <div className="animate-float">
                <Sun size={64} className="text-yellow-300 lg:w-20 lg:h-20"
                  style={{ filter: "drop-shadow(0 0 15px rgba(255, 200, 0, 0.5))" }}
                />
              </div>
            ) : (
              <div className="animate-float">
                <Moon size={64} className="text-gray-200 lg:w-20 lg:h-20"
                  style={{ filter: "drop-shadow(0 0 15px rgba(180, 180, 255, 0.3))" }}
                />
              </div>
            )}
          </div>

          {/* Time and date */}
          <div className="text-center mb-2">
            <div className="flex items-center justify-center space-x-2">
              <Clock size={16} className="text-white/80" />
              <p className="text-white/80 text-lg">{formatTime(locationTime)}</p>
            </div>
            <p className="text-white/60 text-sm">{formatDate(locationTime)}</p>
          </div>

          {/* Heading with mode toggle */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <h1 className="text-2xl lg:text-4xl font-bold text-white text-center">
              {specialCase === 'polarDay' ? 'Midnight Sun' :
               specialCase === 'polarNight' ? 'Polar Night' :
               currentCountdown.label}
            </h1>
            {!specialCase && (
              <button
                onClick={() => setCountdownMode(prev => prev === 'prayer' ? 'sun' : 'prayer')}
                className="text-white/50 hover:text-white/80 transition-colors duration-200"
                aria-label={`Switch to ${countdownMode === 'prayer' ? 'sunrise/sunset' : 'prayer times'} countdown`}
                title={countdownMode === 'prayer' ? 'Switch to Sunrise/Sunset' : 'Switch to Fajr/Maghrib'}
              >
                {countdownMode === 'prayer'
                  ? <ToggleRight size={24} className="text-white/60 hover:text-white/90" />
                  : <ToggleLeft size={24} className="text-white/40 hover:text-white/70" />}
              </button>
            )}
          </div>

          {/* Countdown - BIG */}
          <div className="text-5xl lg:text-7xl font-bold text-white text-center mb-6 lg:mb-8 tabular-nums">
            {specialCase ? '∞' : currentCountdown.countdown}
          </div>

          {/* Progress bar - wider on kiosk */}
          <div className="w-full max-w-sm lg:max-w-2xl">
            <SunProgressBar
              times={times}
              prayerTimes={prayerTimes}
              showPrayerMarkers={showPrayerMarkers}
              timezone={prayerTimes?.timezone}
            />
          </div>

          {/* Location button */}
          <button
            className="mt-4 flex items-center justify-center px-6 py-3 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-colors duration-200 min-h-[44px]"
            onClick={() => setShowLocationModal(true)}
            aria-label={`Change location, currently ${coordinates.name}`}
          >
            <MapPin size={18} className="mr-2" />
            {coordinates.name}
          </button>
        </div>

        {/* RIGHT PANEL: Info (always visible on kiosk, hidden on mobile) */}
        <div className="hidden lg:flex flex-col w-[380px] backdrop-blur-md bg-black/40 rounded-xl border border-white/15 p-4 overflow-y-auto">
          <AdditionalInfoContainer
            coordinates={coordinates}
            additionalInfo={additionalInfo}
            onPrayerTimesLoaded={() => {}}
            showPrayerMarkers={showPrayerMarkers}
            setShowPrayerMarkers={setShowPrayerMarkers}
            forceExpanded={true}
            timezone={prayerTimes?.timezone}
            weatherData={weatherData}
            weatherLoading={weatherLoading}
          />
        </div>

        {/* MOBILE: Accordion below main content */}
        <div className="lg:hidden">
          <AdditionalInfoContainer
            coordinates={coordinates}
            additionalInfo={additionalInfo}
            onPrayerTimesLoaded={() => {}}
            showPrayerMarkers={showPrayerMarkers}
            setShowPrayerMarkers={setShowPrayerMarkers}
            timezone={prayerTimes?.timezone}
            weatherData={weatherData}
            weatherLoading={weatherLoading}
          />
        </div>
      </div>

      {/* Toast error notification */}
      {errorMessage && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-red-500/90 text-white px-6 py-3 rounded-lg shadow-lg z-50">
          {errorMessage}
        </div>
      )}

      <LocationModal
        show={showLocationModal}
        onClose={() => setShowLocationModal(false)}
        locationInput={locationInput}
        setLocationInput={setLocationInput}
        searchResults={searchResults}
        isLoading={isLoading}
        handleLocationChange={handleLocationChange}
        selectLocation={selectLocation}
        getCurrentLocation={getCurrentLocation}
      />
    </div>
  );
};

export default SunriseSunsetApp;
